import type {
  DynamicDiscount,
  DynamicGift,
  DynamicPriceThreshold
} from '@simplisafe/ss-ecomm-data/prices/service'
import * as O from 'fp-ts/lib/Option'
import * as A from 'fp-ts/lib/ReadonlyArray'
import { pipe } from 'fp-ts/lib/function'
import { divideByFractionDigits } from './priceHelpers'

export const convertThresholds = (
  threshold: DynamicPriceThreshold,
  fractionDigits = 2
) => ({
  ...threshold,
  lowerThreshold: divideByFractionDigits(
    threshold.lowerThreshold,
    fractionDigits
  ),
  upperThreshold: divideByFractionDigits(
    threshold.upperThreshold,
    fractionDigits
  )
})

export const itMatchesGiftThreshold = (
  totalPrice: number,
  { lowerThreshold, upperThreshold }: DynamicGift['threshold']
) => {
  return totalPrice >= lowerThreshold && totalPrice < upperThreshold
}

export const checkMatchedGiftThreshold =
  (totalPrice?: number) =>
  ({ isMatchingThreshold, ...gift }: DynamicGift) => ({
    ...gift,
    isMatchingThreshold: totalPrice
      ? itMatchesGiftThreshold(totalPrice, gift.threshold)
      : isMatchingThreshold
  })

export const sortGiftsByThreshold = (
  dynamicGifts: ReadonlyArray<DynamicGift>
) =>
  dynamicGifts.toSorted(
    (a, b) => a.threshold.lowerThreshold - b.threshold.lowerThreshold
  )

/**
 * Recalculates the thresholds for dynamic gifts based on active dynamic discounts.
 *
 * This function adjusts the `lowerThreshold` and `upperThreshold` of each dynamic gift
 * by applying the corresponding discount value from the list of dynamic discounts.
 *
 * If no matching discount is found for a gift, the original thresholds are retained.
 *
 *  Example:
 *   - Original lowerThreshold: 600
 *   - Discount: 50% (value = 50)
 *   - Result: 600 * 0.5 = 300
 */
export const recalculateThresholds =
  (dynamicDiscounts: O.Option<readonly DynamicDiscount[]>) =>
  (dynamicGifts: readonly DynamicGift[]) =>
    pipe(
      dynamicGifts,
      A.map(gift => {
        const { lowerThreshold, upperThreshold } = gift.threshold

        return pipe(
          dynamicDiscounts,
          O.flatMap(
            A.findFirst(
              discount => discount.threshold.lowerThreshold === lowerThreshold
            )
          ),
          O.map(discount => discount.value),
          O.map(discountValue => ({
            lowerThreshold: Math.floor(lowerThreshold * (discountValue / 100)),
            upperThreshold: Math.floor(upperThreshold * (discountValue / 100))
          })),
          O.getOrElse(() => ({
            lowerThreshold,
            upperThreshold
          })),
          (updatedThresholds): DynamicGift => ({
            ...gift,
            threshold: {
              ...gift.threshold,
              ...updatedThresholds
            }
          })
        )
      })
    )
